import {
  Box,
  Checkbox,
  CheckboxGroup,
  DateInput,
  Divider,
  Input,
  Select,
  Text,
  Toggle,
  dateInputDateFormat,
} from '@bitrise/bitkit';
import { useFormContext } from 'react-hook-form';
import { DateTime } from 'luxon';
import TimeZoneDropdown from '@/pages/App/AppDetails/components/TimeZoneDropdown/TimeZoneDropdown';
import { Day } from '../StartBuildDialog/utils/daysToNumeric';
import { BasicScheduleBuildType } from '../StartBuildDialog/types';

const nthWeekdayOfMonth = (date: DateTime): number => {
  return Math.floor((date.day - 1) / 7) + 1;
};

export const getWeekOfMonthStr = (date: DateTime) => {
  const numberToString: Record<number, string> = {
    1: 'first',
    2: 'second',
    3: 'third',
    4: 'fourth',
    5: 'fifth',
  };
  return numberToString[nthWeekdayOfMonth(date)];
};

const BasicBuildSchedule = () => {
  const { register, watch, setValue } = useFormContext<BasicScheduleBuildType>();

  const isRepetitionToggled = watch('repetitionEnabled');
  const repeatEvery = watch('repeatEvery');
  const repeatInterval = watch('repeatInterval');
  const startDate = watch('startDate');

  const dateFormatter = DateTime.fromFormat(startDate || '', dateInputDateFormat);

  const suffix = repeatEvery && repeatEvery > 1 ? 's' : '';

  return (
    <div>
      <Box display="flex" gap="16" marginBottom="24">
        <DateInput
          value={startDate}
          onChange={(date) => {
            if (date) {
              setValue('startDate', date);
            }
          }}
          label="Start date and time"
          isRequired
          flex="1"
          data-testid="start-date"
        />
        <Input {...register('time')} isRequired label="Time" type="time" flex="1" data-testid="time" />
      </Box>
      <TimeZoneDropdown
        data-testid="timezone"
        locale={watch('setupLocation') || ''}
        onTimeZoneChange={(timeZone) => {
          setValue('setupLocation', timeZone);
        }}
      />
      <Divider marginY="24" />
      <Text textStyle="body/md/semibold" marginBottom="4">
        Repetition
      </Text>
      <Toggle
        data-testid="repetition-enabled"
        valueText={isRepetitionToggled ? 'Enabled' : 'Disabled'}
        isChecked={isRepetitionToggled}
        onChange={(event) => {
          setValue('repetitionEnabled', event.target.checked);
        }}
      />
      {isRepetitionToggled && (
        <>
          <Box display="flex" alignItems="flex-end" gap="16" marginTop="24">
            <Input
              {...register('repeatEvery')}
              label="Repeat every"
              isRequired
              width="136px"
              type="number"
              data-testid="repeat-every"
              min={1}
              max={100}
            />
            <Select {...register('repeatInterval')} width="136px" data-testid="repeat-interval">
              <option key="minute" value="minute">
                minute{suffix}
              </option>
              <option key="hour" value="hour">
                hour{suffix}
              </option>
              <option key="day" value="day">
                day{suffix}
              </option>
              <option key="week" value="week">
                week{suffix}
              </option>
              <option key="month" value="month">
                month{suffix}
              </option>
              <option key="year" value="year">
                year{suffix}
              </option>
            </Select>
            {repeatInterval === 'month' && (
              <Select {...register('monthlyRepetition')} width="288px" data-testid="day-select">
                <option key="day" value="day">
                  On day {dateFormatter.day}
                </option>
                <option key="weekday" value="weekday">
                  On the {getWeekOfMonthStr(dateFormatter)} {dateFormatter.weekdayLong}
                </option>
              </Select>
            )}
            {repeatInterval === 'year' && (
              <Select {...register('yearlyRepetition')} width="288px" data-testid="day-select">
                <option key="day" value="day">
                  On {dateFormatter.monthShort} {dateFormatter.day}
                </option>
                <option key="weekday" value="weekday">
                  On the {getWeekOfMonthStr(dateFormatter)} {dateFormatter.weekdayLong} of {dateFormatter.monthLong}
                </option>
              </Select>
            )}
          </Box>
          {repeatInterval === 'week' && (
            <Box marginTop="24">
              <Text textStyle="body/md/semibold">Repeat on</Text>
              <CheckboxGroup
                value={watch('repeatOn')}
                display="flex"
                gap="16"
                onChange={(days: Day[]) => setValue('repeatOn', days)}
                paddingY="12"
                flexWrap="wrap"
              >
                <Checkbox value="MO">Mon</Checkbox>
                <Checkbox value="TU">Tue</Checkbox>
                <Checkbox value="WE">Wed</Checkbox>
                <Checkbox value="TH">Thu</Checkbox>
                <Checkbox value="FR">Fri</Checkbox>
                <Checkbox value="SA">Sat</Checkbox>
                <Checkbox value="SU">Sun</Checkbox>
              </CheckboxGroup>
            </Box>
          )}
        </>
      )}
    </div>
  );
};

export default BasicBuildSchedule;
